import { FormGroup } from '@angular/forms';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { ResourceBuilderComponent } from '../resource-builder.component';
import { ResourceBuilderService } from '../resource-builder.service';

/**
 * Nested test/survey builders own a separate form from the parent course
 * (or course-plan) item row. Parent Save only watches that row, so inner
 * question edits must dirty it and save with the parent builder submit.
 */
export class NestedResourceBuilderBinding {
    private readonly bindDestroy = new Subject<void>();

    bind(
        nestedBuilder: ResourceBuilderComponent | undefined,
        parentBuilderService: ResourceBuilderService | null,
        parentItemGroup: FormGroup | undefined
    ): void {
        this.bindDestroy.next();

        if (!nestedBuilder || !parentBuilderService || !parentItemGroup) {
            return;
        }

        const nestedForm = nestedBuilder.getCurrentForm();
        if (!nestedForm) {
            return;
        }

        this.markParentDirtyWhenNestedEdits(nestedForm, parentItemGroup);
        this.submitNestedWhenParentSaves(nestedBuilder, parentBuilderService);
    }

    destroy(): void {
        this.bindDestroy.next();
        this.bindDestroy.complete();
    }

    private markParentDirtyWhenNestedEdits(nestedForm: FormGroup, parentItemGroup: FormGroup): void {
        nestedForm.valueChanges
            .pipe(
                filter(() => nestedForm.dirty && parentItemGroup.pristine),
                takeUntil(this.bindDestroy)
            )
            .subscribe(() => {
                parentItemGroup.markAsDirty();
                parentItemGroup.updateValueAndValidity();
            });
    }

    private submitNestedWhenParentSaves(
        nestedBuilder: ResourceBuilderComponent,
        parentBuilderService: ResourceBuilderService
    ): void {
        parentBuilderService
            .onSubmit()
            .pipe(takeUntil(this.bindDestroy))
            .subscribe(() => {
                if (nestedBuilder.getCurrentForm()?.dirty) {
                    nestedBuilder.submit({ emitSaved: false });
                }
            });
    }
}
